"use client";

/**
 * Enrollments tab data hook (Phase J · Task 56).
 *
 * Composes the Orval-generated enrollment endpoints into a single
 * surface the table + row actions consume. The list query plus the
 * three mutations (pause / resume / cancel) all share an
 * `invalidate()` helper keyed off the canonical
 * `getListEnrollmentsQueryKey` so any successful action immediately
 * refetches the visible page.
 *
 * Spec refs:
 * - §10.1 list endpoint, §10.4 action semantics.
 * - §13.1 (`Enrollments.Filters`, `Enrollments.RowActions`).
 *
 * Per AGENTS.md Frontend Conventions: every server-state interaction
 * goes through Orval-generated hooks. We do NOT hand-roll
 * `useQuery`/`useMutation` here — the hook only composes existing
 * generated primitives plus shared invalidation.
 */

import { useCallback, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  getListEnrollmentsQueryKey,
  useCancelEnrollment,
  useListEnrollments,
  usePauseEnrollment,
  useResumeEnrollment,
} from "@/lib/api/generated";

import type {
  EnrollmentListItem,
  EnrollmentStatus,
  ListEnrollmentsParams,
} from "../lib/types";

export const ENROLLMENTS_PER_PAGE = 25;

export interface UseEnrollmentsListArgs {
  jobId: string;
  sequenceId: string;
  /** Single status filter (matches the API which accepts one value).
   *  `null` means "show all statuses". */
  status?: EnrollmentStatus | null;
  page?: number;
  /** Override the default page size — the table doesn't expose this
   *  but tests use it for deterministic pagination. */
  perPage?: number;
}

export interface UseEnrollmentsListResult {
  rows: EnrollmentListItem[];
  total: number;
  page: number;
  perPage: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  invalidate: () => Promise<void>;
  pause: ReturnType<typeof usePauseEnrollment>;
  resume: ReturnType<typeof useResumeEnrollment>;
  cancel: ReturnType<typeof useCancelEnrollment>;
}

export function useEnrollmentsList(
  args: UseEnrollmentsListArgs,
): UseEnrollmentsListResult {
  const {
    jobId,
    sequenceId,
    status = null,
    page = 1,
    perPage = ENROLLMENTS_PER_PAGE,
  } = args;

  const queryClient = useQueryClient();

  const params = useMemo<ListEnrollmentsParams>(
    () => ({
      status: status ?? undefined,
      page,
      per_page: perPage,
    }),
    [status, page, perPage],
  );

  const list = useListEnrollments(jobId, sequenceId, params, {
    query: { enabled: Boolean(jobId && sequenceId) },
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: getListEnrollmentsQueryKey(jobId, sequenceId),
      exact: false,
    });
  }, [queryClient, jobId, sequenceId]);

  const onSuccess = useCallback(() => {
    void invalidate();
  }, [invalidate]);

  const pause = usePauseEnrollment({ mutation: { onSuccess } });
  const resume = useResumeEnrollment({ mutation: { onSuccess } });
  const cancel = useCancelEnrollment({ mutation: { onSuccess } });

  const rows = useMemo<EnrollmentListItem[]>(
    () => list.data?.enrollments ?? [],
    [list.data?.enrollments],
  );

  return {
    rows,
    total: list.data?.total ?? 0,
    page,
    perPage,
    isLoading: list.isLoading,
    isFetching: list.isFetching,
    isError: list.isError,
    invalidate,
    pause,
    resume,
    cancel,
  };
}
